import AsyncStorage from '@react-native-async-storage/async-storage'
import { Log, type StorageProvider } from '@statsig/react-bindings'

// Vendored from @statsig/react-native-core's PreloadedAsyncStorage, unchanged
// except for sourcing `Log`/`StorageProvider` from `@statsig/react-bindings`
// (which re-exports `@statsig/client-core`) so there's a single statsig copy.
//
// Statsig's storage API is synchronous, but AsyncStorage is async. This bridges
// them: prefetch all `statsig.*` keys into an in-memory mirror once, serve reads
// synchronously from the mirror, and write through to AsyncStorage in the
// background. Persistence (warm cache across launches) without any native
// module beyond AsyncStorage.

const NOT_READY_ERROR =
  'Attempting to alter AsyncStorage before it is ready. Values will not be persisted.'

export function createPreloadedAsyncStorage(): StorageProvider {
  const inMemoryStore: Record<string, string> = {}
  let isResolved = false
  let readyPromise: Promise<void> | null = null

  const enforceIsResolved = () => {
    if (!isResolved) {
      Log.error(NOT_READY_ERROR)
    }

    return isResolved
  }

  const provider: StorageProvider = {
    getProviderName: (): string => 'AsyncStorage',

    isReady: () => isResolved,

    isReadyResolver: () => {
      if (!readyPromise) {
        readyPromise = prefetchFromAsyncStorage(inMemoryStore).finally(() => {
          isResolved = true
        })
      }
      return readyPromise
    },

    getItem: (key: string): string | null => {
      if (!enforceIsResolved()) {
        return null
      }

      return inMemoryStore[key] ?? null
    },

    setItem: (key: string, value: string): void => {
      inMemoryStore[key] = value
      if (!enforceIsResolved()) {
        return
      }

      AsyncStorage.setItem(key, value).catch(Log.error)
    },

    removeItem: (key: string): void => {
      delete inMemoryStore[key]
      if (!enforceIsResolved()) {
        return
      }

      AsyncStorage.removeItem(key).catch(Log.error)
    },

    getAllKeys: (): readonly string[] => {
      if (!enforceIsResolved()) {
        return []
      }

      return Object.keys(inMemoryStore)
    },
  }

  return provider
}

async function prefetchFromAsyncStorage(
  inMemoryStore: Record<string, string>,
): Promise<void> {
  const keys = await AsyncStorage.getAllKeys()

  await Promise.all(
    keys.map(async (key) => {
      if (!key.startsWith('statsig.')) {
        return
      }

      const cache = await AsyncStorage.getItem(key)
      if (!cache) {
        return
      }

      inMemoryStore[key] = cache
    }),
  )
}
